/**
 * Utilitario para impressao de etiquetas autocolantes - V2 Design Profissional
 */

export interface TamanhoEtiqueta {
  id: string
  nome: string
  largura: number
  altura: number
}

export const TAMANHOS_ETIQUETA: TamanhoEtiqueta[] = [
  { id: '38x90', nome: '38mm x 90mm (Padrao)', largura: 90, altura: 38 },
  { id: '25x50', nome: '25mm x 50mm (Pequena)', largura: 50, altura: 25 },
  { id: '50x100', nome: '50mm x 100mm (Media)', largura: 100, altura: 50 },
  { id: '62x100', nome: '62mm x 100mm (Grande)', largura: 100, altura: 62 },
  { id: '29x90', nome: '29mm x 90mm (Fina)', largura: 90, altura: 29 },
  { id: '38x62', nome: '38mm x 62mm (Quadrada)', largura: 62, altura: 38 },
  { id: 'custom', nome: 'Personalizado', largura: 0, altura: 0 },
]

export interface DadosEtiqueta {
  nomeEmpresa: string
  numeroOS: string
  nomeCliente: string
  dataEntrada?: string
  horaEntrada?: string
  equipamento?: string
  telefone?: string
}

export interface ConfiguracaoEtiqueta {
  tamanho: TamanhoEtiqueta
  larguraCustom?: number
  alturaCustom?: number
  mostrarData?: boolean
  mostrarEquipamento?: boolean
  mostrarTelefone?: boolean
}

const calcularFontes = (altura: number) => {
  const baseSize = altura / 38
  return {
    empresa: Math.max(7, Math.min(12, Math.round(10 * baseSize))),
    os: Math.max(12, Math.min(22, Math.round(18 * baseSize))),
    cliente: Math.max(7, Math.min(12, Math.round(9 * baseSize))),
    info: Math.max(6, Math.min(9, Math.round(7 * baseSize))),
    label: Math.max(5, Math.min(7, Math.round(6 * baseSize)))
  }
}

const truncarTexto = (texto: string, maxLength: number): string => {
  if (!texto) return ''
  if (texto.length <= maxLength) return texto
  return texto.substring(0, maxLength - 2) + '..'
}

export const gerarHtmlEtiqueta = (dados: DadosEtiqueta, config: ConfiguracaoEtiqueta): string => {
  const largura = config.tamanho.id === 'custom' && config.larguraCustom ? config.larguraCustom : config.tamanho.largura
  const altura = config.tamanho.id === 'custom' && config.alturaCustom ? config.alturaCustom : config.tamanho.altura
  const fontes = calcularFontes(altura)
  const padding = Math.max(1.5, Math.round(altura * 0.05))
  const headerHeight = Math.max(4, Math.round(altura * 0.18))
  const borderRadius = Math.max(1, Math.round(altura * 0.05))

  const maxChars = Math.floor(largura / 4)
  const nomeEmpresa = truncarTexto(dados.nomeEmpresa, maxChars)
  const nomeCliente = truncarTexto(dados.nomeCliente, maxChars)
  const equipamento = dados.equipamento ? truncarTexto(dados.equipamento, maxChars) : ''
  const telefone = dados.telefone ? truncarTexto(dados.telefone, 15) : ''

  // Monta as informações extras
  let infoExtras = ''
  if (config.mostrarEquipamento && equipamento) {
    infoExtras += `<div class="info-row"><span class="label">Equip:</span> <span class="value">${equipamento}</span></div>`
  }
  if (config.mostrarData && dados.dataEntrada) {
    const dataHora = dados.horaEntrada ? `${dados.dataEntrada} ${dados.horaEntrada}` : dados.dataEntrada
    infoExtras += `<div class="info-row"><span class="label">Entrada:</span> <span class="value">${dataHora}</span></div>`
  }
  if (config.mostrarTelefone && telefone) {
    infoExtras += `<div class="info-row"><span class="label">Tel:</span> <span class="value">${telefone}</span></div>`
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Etiqueta OS ${dados.numeroOS}</title>
  <style>
    @page {
      size: ${largura}mm ${altura}mm;
      margin: 0;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .etiqueta {
      width: ${largura}mm;
      height: ${altura}mm;
      border: 0.3mm solid #333;
      border-radius: ${borderRadius}mm;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    .header {
      background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
      color: white;
      padding: ${padding * 0.5}mm ${padding}mm;
      height: ${headerHeight}mm;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .empresa-nome {
      font-size: ${fontes.empresa}pt;
      font-weight: 600;
      letter-spacing: 0.3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 70%;
    }
    .os-badge {
      background: rgba(255,255,255,0.95);
      color: #1e40af;
      padding: 0.5mm 2mm;
      border-radius: 1mm;
      font-size: ${fontes.info}pt;
      font-weight: 700;
    }
    .content {
      flex: 1;
      padding: ${padding}mm;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .os-numero {
      text-align: center;
      font-size: ${fontes.os}pt;
      font-weight: 800;
      color: #111;
      letter-spacing: 1px;
      border-bottom: 0.3mm solid #e5e7eb;
      padding-bottom: ${padding * 0.5}mm;
      margin-bottom: ${padding * 0.5}mm;
    }
    .os-numero span {
      color: #1e40af;
    }
    .cliente-section {
      text-align: center;
    }
    .cliente-label {
      font-size: ${fontes.label}pt;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.3mm;
    }
    .cliente-nome {
      font-size: ${fontes.cliente}pt;
      font-weight: 600;
      color: #1f2937;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .info-section {
      margin-top: auto;
      border-top: 0.2mm dashed #d1d5db;
      padding-top: ${padding * 0.4}mm;
    }
    .info-row {
      display: flex;
      justify-content: center;
      gap: 1mm;
      font-size: ${fontes.info}pt;
      color: #4b5563;
      line-height: 1.3;
    }
    .info-row .label {
      color: #9ca3af;
      font-weight: 500;
    }
    .info-row .value {
      font-weight: 600;
      color: #374151;
    }
    @media print {
      body {
        width: ${largura}mm;
        height: ${altura}mm;
      }
      .etiqueta {
        border: none;
        border-radius: 0;
      }
    }
  </style>
</head>
<body>
  <div class="etiqueta">
    <div class="header">
      <div class="empresa-nome">${nomeEmpresa}</div>
      <div class="os-badge">OS</div>
    </div>
    <div class="content">
      <div class="os-numero"><span>#</span>${dados.numeroOS}</div>
      <div class="cliente-section">
        <div class="cliente-label">Cliente</div>
        <div class="cliente-nome">${nomeCliente}</div>
      </div>
      ${infoExtras ? `<div class="info-section">${infoExtras}</div>` : ''}
    </div>
  </div>
</body>
</html>`
}

export const imprimirEtiqueta = async (dados: DadosEtiqueta, config: ConfiguracaoEtiqueta): Promise<void> => {
  const html = gerarHtmlEtiqueta(dados, config)
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:0;height:0'
  document.body.appendChild(iframe)
  const iframeDoc = iframe.contentWindow?.document
  if (!iframeDoc) {
    document.body.removeChild(iframe)
    throw new Error('Erro ao criar documento')
  }
  iframeDoc.open()
  iframeDoc.write(html)
  iframeDoc.close()
  await new Promise(r => setTimeout(r, 300))
  try {
    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
  } finally {
    setTimeout(() => document.body.removeChild(iframe), 1000)
  }
}

export const preVisualizarEtiqueta = (dados: DadosEtiqueta, config: ConfiguracaoEtiqueta): void => {
  const html = gerarHtmlEtiqueta(dados, config)
  const novaJanela = window.open('', '_blank', 'width=450,height=250,scrollbars=no,resizable=yes')
  if (novaJanela) {
    novaJanela.document.open()
    novaJanela.document.write(html)
    novaJanela.document.close()
  }
}

export const getConfiguracaoPadrao = (): ConfiguracaoEtiqueta => ({
  tamanho: TAMANHOS_ETIQUETA[0],
  mostrarData: true,
  mostrarEquipamento: true,
  mostrarTelefone: false,
})

export const salvarConfiguracaoEtiqueta = (config: ConfiguracaoEtiqueta): void => {
  localStorage.setItem('etiqueta_config', JSON.stringify({
    tamanhoId: config.tamanho.id,
    larguraCustom: config.larguraCustom,
    alturaCustom: config.alturaCustom,
    mostrarData: config.mostrarData,
    mostrarEquipamento: config.mostrarEquipamento,
    mostrarTelefone: config.mostrarTelefone,
  }))
}

export const carregarConfiguracaoEtiqueta = (): ConfiguracaoEtiqueta => {
  const saved = localStorage.getItem('etiqueta_config')
  if (!saved) return getConfiguracaoPadrao()
  try {
    const parsed = JSON.parse(saved)
    const tamanho = TAMANHOS_ETIQUETA.find(t => t.id === parsed.tamanhoId) || TAMANHOS_ETIQUETA[0]
    return {
      tamanho,
      larguraCustom: parsed.larguraCustom,
      alturaCustom: parsed.alturaCustom,
      mostrarData: parsed.mostrarData ?? true,
      mostrarEquipamento: parsed.mostrarEquipamento ?? true,
      mostrarTelefone: parsed.mostrarTelefone ?? false
    }
  } catch {
    return getConfiguracaoPadrao()
  }
}
